"use client";

import { Logo } from "@sum-video/shared/components/Logo";
import { Button } from "@sum-video/shared/components/ui/button";
import { useTranslations } from "next-intl";
import type { JSX } from "react";
import { Link, usePathname } from "@/i18n/navigation";
import { BLOG_SECTIONS, sectionHubPathname } from "@/lib/blog/sections";

export function Footer(): JSX.Element | null {
  const t = useTranslations("Footer");
  const sectionsT = useTranslations("BlogSections");
  const pathname = usePathname();

  if (pathname === "/" || pathname.startsWith("/onboarding")) {
    return null;
  }

  return (
    <footer className="border-t">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <Logo className="mb-4" LinkComponent={Link} />
            <p className="text-base text-muted-foreground">
              {t("description")}
            </p>
            <div className="text-xs text-muted-foreground mt-4">
              <p>{t("company")}</p>
              <p>{t("address")}</p>
              <p>{t("taxNumber")}</p>
            </div>
          </div>

          {/* Product */}
          <div>
            <h2 className="font-semibold mb-4">{t("product")}</h2>
            <ul className="space-y-2">
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/how-it-works">{t("howItWorks")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/pricing">{t("pricing")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/affiliate">{t("affiliate")}</Link>
                </Button>
              </li>
              {BLOG_SECTIONS.map((section) => (
                <li key={section}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                    asChild
                  >
                    <Link href={sectionHubPathname(section)}>
                      {sectionsT(`${section}.name`)}
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h2 className="font-semibold mb-4">{t("support")}</h2>
            <ul className="space-y-2">
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/about">{t("about")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/contact">{t("contactUs")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/dashboard">{t("dashboard")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/feature-requests">{t("featureRequests")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/bug-reports">{t("bugReports")}</Link>
                </Button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-semibold mb-4">{t("legal")}</h2>
            <ul className="space-y-2">
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/terms">{t("termsOfService")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/privacy">{t("privacyPolicy")}</Link>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <Link href="/cookies">{t("cookiePolicy")}</Link>
                </Button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-base text-muted-foreground">
            {t("copyright", { year: new Date().getFullYear() })}
          </p>
          <p className="text-base text-muted-foreground mt-2 md:mt-0">
            {t("madeWith")}
          </p>
        </div>
      </div>
    </footer>
  );
}
